// import Footer from "../../components/Footer"
import NavbarPrivate from "../../components/navbar/NavbarPrivate"
import {
  PublicCourseSchema,
  PublicLessonSchema,
  PublicSectionSchema,
  UserCourseSchema,
  UserLabSchema,
} from "../../api"
import useAuthedApi from "../../hooks/useAuthedApi"
import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { isAxiosError } from "axios"
import useToasts from "../../hooks/useToasts"
import Loading from "../../components/Loading"
import {
  Accordion,
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Modal,
  Tooltip,
} from "flowbite-react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import ClassroomSidebar from "../../components/sidebar/ClassroomSidebar"
import ReactPlayer from "react-player/"
import {
  BiCheckCircle,
  BiDownload,
  BiLinkAlt,
  BiLinkExternal,
} from "react-icons/bi"
import { LuPartyPopper } from "react-icons/lu"
import ClassroomFlag from "../../components/ClassroomFlag"
import { FaFlask } from "react-icons/fa"

const Classroom = () => {
  const { toastList, setToastList } = useToasts()
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    window.innerWidth < 640
  )
  const [course, setCourse] = useState<PublicCourseSchema>()
  const [courseData, setCourseData] = useState<UserCourseSchema>()
  const [labData, setLabData] = useState<UserLabSchema>()
  const [section, setSection] = useState<PublicSectionSchema>()
  const [lesson, setLesson] = useState<PublicLessonSchema>()
  const [startSecond, setStartSecond] = useState<number>(0)
  const [secondsLoaded, setSecondsLoaded] = useState(false)
  const [isCompleted, setIsCompleted] = useState<boolean>(false)
  const [allFlagsCompleted, setAllFlagsCompleted] = useState<boolean>(false)
  const [completedFlagIds] = useState<string[]>([])
  const [videoURL, setVideoURL] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [openCourseCompleteModal, setOpenCourseCompleteModal] =
    useState<boolean>(false)
  const { CoursesAuthedApi, LabsAuthedApi } = useAuthedApi()
  const navigate = useNavigate()
  const { slug } = useParams()
  const [errMsg, setErrMsg] = useState("")
  const errRef = useRef<HTMLInputElement>(null)
  const [errCompleteMsg, setErrCompleteMsg] = useState("")
  const errCompleteRef = useRef<HTMLInputElement>(null)
  const { hash } = useLocation()

  const initClass = async (slug: string) => {
    let _course
    let _section
    let _lesson
    let _second

    setErrMsg("")
    setErrCompleteMsg("")
    setAllFlagsCompleted(false)

    // Get course data
    try {
      const response = await CoursesAuthedApi.coursesReadCourse(slug)
      if (!response.data) {
        setErrMsg("Get course failed with unknown error")
        errRef.current?.focus()
        return
      }
      // console.log(JSON.stringify(response?.data))
      _course = response.data
      setCourse(_course)
      if (!hash) {
        if (_course.sections !== undefined && _course.sections !== null) {
          navigate(
            `#${_course.sections[0].slug}/${_course.sections[0].lessons[0].slug}`
          )
        }
        return
      } else {
        // Get section from url hash
        let hash_trimmed = hash.substring(1)
        if (_course.sections !== undefined && _course.sections !== null) {
          _section = _course.sections.find((s) => {
            return s.slug === hash_trimmed.split("/")[0]
          })
          if (!_section) {
            setErrMsg("This section does not exist in this course")
            errRef.current?.focus()
            return
          }
          setSection(_section)

          // Get lesson from url hash
          _lesson = _section.lessons.find((s) => {
            return s.slug === hash_trimmed.split("/")[1].split("?")[0]
          })
          if (!_lesson) {
            setErrMsg(
              "This lesson does not exist in this section of the course"
            )
            errRef.current?.focus()
            return
          }
          setLesson(_lesson)
        }

        // Get second from url hash
        if (hash_trimmed.split("/")[1].split("?").length === 2) {
          _second = hash_trimmed.split("/")[1].split("?")[1].split("second=")[1]
        }

        if (_second) {
          setStartSecond(parseInt(_second))
        }
      }
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrMsg("No server response")
        } else if (err.response?.status === 404) {
          setErrMsg("We could not find that course")
        } else if (err.response?.status === 422) {
          setErrMsg("Data validation error")
        } else {
          setErrMsg("Get course failed with unknown error")
        }
      } else {
        console.log(err)
        setErrMsg("Get course failed with unknown error")
      }
      errRef.current?.focus()
    }

    // Get video presigned link
    try {
      if (!_lesson || !_section || !_course) {
        setErrMsg("Get video url failed missing course, section or lesson")
        errRef.current?.focus()
        return
      }
      const response = await CoursesAuthedApi.coursesGetVideoPresignedUrl(
        slug,
        _section.slug,
        _lesson.slug
      )
      if (!response.data) {
        setErrMsg("Get video url failed with unknown error")
        errRef.current?.focus()
        return
      }
      // console.log(JSON.stringify(response?.data))

      setVideoURL(response.data.presigned_url)
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrMsg("No server response")
        } else if (err.response?.status === 404) {
          setErrMsg("We could not find that video url")
        } else if (err.response?.status === 422) {
          setErrMsg("Data validation error")
        } else {
          setErrMsg("Get video url failed with unknown error")
        }
      } else {
        setErrMsg("Get video url failed with unknown error")
      }
      errRef.current?.focus()
    }

    // Get user completion data
    try {
      if (!_lesson || !_section || !_course) {
        setErrMsg("Get course data failed missing course, section or lesson")
        return
      }
      const response = await CoursesAuthedApi.coursesReadCourseDataMe(slug)
      if (!response.data) {
        setErrMsg("Get course data failed with unknown error")
        return
      }
      // console.log(JSON.stringify(response?.data))

      setCourseData(response.data)
      if (
        (response.data.data as any).sections &&
        (response.data.data as any).sections[_section.id] &&
        (response.data.data as any).sections[_section.id].lessons &&
        (response.data.data as any).sections[_section.id].lessons[_lesson.id] &&
        (response.data.data as any).sections[_section.id].lessons[_lesson.id]
          .completed
      ) {
        setIsCompleted(true)
      }
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrMsg("No server response")
        } else if (err.response?.status === 404) {
          setErrMsg("We could not find that course data")
        } else if (err.response?.status === 422) {
          setErrMsg("Data validation error")
        } else {
          setErrMsg("Get course data failed with unknown error")
        }
      } else {
        setErrMsg("Get course data failed with unknown error")
      }
      errRef.current?.focus()
    }

    // Get user lab completion data
    try {
      if (!_course) {
        setErrMsg("Get lab data failed missing course")
        errRef.current?.focus()
        return
      }
      const response = await LabsAuthedApi.labsReadLabMe(slug)
      if (!response.data) {
        setErrMsg("Get lab data failed with unknown error")
        errRef.current?.focus()
        return
      }
      // console.log(JSON.stringify(response?.data))

      setLabData(response.data)
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrMsg("No server response")
        } else if (err.response?.status === 404) {
          setErrMsg("We could not find that lab data")
        } else if (err.response?.status === 422) {
          setErrMsg("Data validation error")
        } else {
          setErrMsg("Get lab data failed with unknown error")
        }
      } else {
        setErrMsg("Get lab data failed with unknown error")
      }
      errRef.current?.focus()
    }

    if (!_lesson) {
      setErrMsg("Get video url failed missing lesson")
      errRef.current?.focus()
      return
    }
    if (_lesson.flags.length === 0) {
      setAllFlagsCompleted(true)
    }
  }

  useEffect(() => {
    if (!slug) {
      navigate("/courses")
      return
    }

    initClass(slug).finally(() => {
      setLoading(false)
    })

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, hash])

  const completeLesson = async () => {
    setErrCompleteMsg("")
    if (!allFlagsCompleted || !course || !section || !lesson) {
      return
    }
    // Post complete lesson
    try {
      const response = await CoursesAuthedApi.coursesCompleteLesson(
        course.slug,
        section.slug,
        lesson.slug
      )
      if (!response.data.success) {
        setErrCompleteMsg("Complete lesson failed with unknown error")
        errCompleteRef.current?.focus()
        return
      }
      // console.log(JSON.stringify(response?.data))
      setCourseData(response.data.data)
      setIsCompleted(true)
      setToastList(
        toastList.concat({
          type: "success",
          children: `Lesson ${lesson.title} is complete!`,
        })
      )

      let courseComplete = false
      if (course.sections !== undefined) {
        courseComplete = course.sections
          ?.map((s: PublicSectionSchema) => {
            return (
              (response.data.data.data as any).sections[s.id] &&
              (response.data.data.data as any).sections[s.id].completed
            )
          })
          .every((s: any) => {
            return s
          })
      }

      setOpenCourseCompleteModal(courseComplete)
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrCompleteMsg("No server response")
        } else if (err.response?.status === 400) {
          setErrCompleteMsg(err.response?.data.detail)
        } else if (err.response?.status === 422) {
          setErrCompleteMsg("Data validation error")
        } else {
          setErrCompleteMsg("Complete lesson failed with unknown error")
        }
      } else {
        setErrCompleteMsg("Complete lesson failed with unknown error")
      }
      errCompleteRef.current?.focus()
    }
  }

  const nextLesson = async () => {
    if (
      !allFlagsCompleted ||
      !isCompleted ||
      !course ||
      !section ||
      !lesson ||
      course.sections === undefined ||
      course.sections === null
    ) {
      return
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
    if (section.lessons[section.lessons.length - 1].id === lesson.id) {
      // If last lesson in section navigate to first lesson of next section
      const nextSectionIndex =
        course.sections.findIndex((s) => {
          return s.id === section.id
        }) + 1
      navigate(
        `#${course.sections[nextSectionIndex].slug}/${course.sections[nextSectionIndex].lessons[0].slug}`
      )
    } else {
      // else get next lesson in current section
      const nextLessonIndex =
        section.lessons.findIndex((l) => {
          return l.id === lesson.id
        }) + 1
      navigate(`#${section.slug}/${section.lessons[nextLessonIndex].slug}`)
    }
  }

  const updateLastSeen = async (second: number) => {
    if (!second || !course || !section || !lesson) {
      return
    }
    try {
      const response = await CoursesAuthedApi.coursesUpdateLastSeen(
        course.slug,
        { lesson: lesson.slug, section: section.slug, second: second }
      )
      if (!response.data.success) {
        setErrMsg("Update last seen failed with unknown error")
        return
      }
      // console.log(JSON.stringify(response?.data))
      setCourseData(response.data.data)
    } catch (err) {
      if (isAxiosError(err)) {
        if (!err?.response) {
          setErrMsg("No server response")
        } else if (err.response?.status === 400) {
          setErrMsg(err.response?.data.detail)
        } else if (err.response?.status === 422) {
          setErrMsg("Data validation error")
        } else {
          setErrMsg("Update last seen failed with unknown error")
        }
      } else {
        setErrMsg("Update last seen failed with unknown error")
      }
      errRef.current?.focus()
    }
  }

  return (
    <>
      <Modal
        show={openCourseCompleteModal}
        dismissible
        size="md"
        popup
        onClose={() => setOpenCourseCompleteModal(false)}
        theme={{
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-neutral-700 flex flex-col max-h-[90vh]",
          },
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <LuPartyPopper className="mx-auto mb-4 h-14 w-14 text-green-600 dark:text-green-400" />
            <h3 className="mb-5 text-lg font-normal text-neutral-500 dark:text-neutral-400">
              Congratulations! You have completed all the lessons in the course
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="success"
                onClick={() => setOpenCourseCompleteModal(false)}
              >
                Go to exam
              </Button>
              <Button
                color="gray"
                onClick={() => setOpenCourseCompleteModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <div className="fixed top-0 inset-x-0 z-40 flex justify-between items-center">
        <NavbarPrivate />
      </div>

      <ClassroomSidebar
        course={course}
        courseData={courseData}
        onCollapseChange={(collapsed) => {
          setSidebarCollapsed(collapsed)
        }}
      />

      {/* Content */}
      <main
        className={
          sidebarCollapsed
            ? "pt-20 pl-0 transition-all ease-in-out duration-300"
            : "pt-20 sm:pl-64 lg:pl-96 transition-all ease-in-out duration-300"
        }
      >
        <div className="container mx-auto">
          {loading ? (
            <Loading />
          ) : (
            <>
              {lesson === undefined ||
              section === undefined ||
              course === undefined ||
              courseData === undefined ? (
                // TODO: make this nice
                <div className="p-5">
                  <Alert
                    className="mx-auto w-1/3"
                    color="failure"
                    icon={ExclamationTriangleIcon}
                    onDismiss={function onDismiss() {
                      setErrMsg("")
                    }}
                  >
                    <span>Could not load lesson</span>
                  </Alert>
                </div>
              ) : (
                <>
                  <div className="p-5">
                    <section className="mb-5" id="classroom">
                      <div className="flex items-center">
                        <Breadcrumb>
                          <Breadcrumb.Item>
                            <p>{course.title}</p>
                          </Breadcrumb.Item>
                          <Breadcrumb.Item>{section.title}</Breadcrumb.Item>
                          <Breadcrumb.Item>{lesson.title}</Breadcrumb.Item>
                        </Breadcrumb>
                        {(courseData.data as any).sections &&
                        (courseData.data as any).sections[section.id] &&
                        (courseData.data as any).sections[section.id].lessons &&
                        (courseData.data as any).sections[section.id].lessons[
                          lesson.id
                        ] &&
                        (courseData.data as any).sections[section.id].lessons[
                          lesson.id
                        ].completed ? (
                          <Badge
                            className="ml-3 px-4 text-sm"
                            icon={BiCheckCircle}
                            color="success"
                          >
                            Completed
                          </Badge>
                        ) : (
                          <></>
                        )}
                      </div>
                      <h2 className="mt-5 mx-3 md:mx-20 text-2xl md:text-3xl font-medium dark:text-white">
                        {lesson.title}
                      </h2>
                      <div className={!errMsg ? "sr-only" : ""}>
                        <Alert
                          className="mt-3"
                          color="failure"
                          icon={ExclamationTriangleIcon}
                          onDismiss={function onDismiss() {
                            setErrMsg("")
                          }}
                        >
                          <span ref={errRef}>{errMsg}</span>
                        </Alert>
                      </div>
                      <div className="mx-3 md:mx-20 mt-6 md:mt-10">
                        <ReactPlayer
                          controls={true}
                          width="100%"
                          height="100%"
                          className="aspect-video"
                          url={videoURL}
                          onReady={(player) => {
                            if (!secondsLoaded) {
                              player.seekTo(startSecond)
                              setSecondsLoaded(true)
                            }
                          }}
                          progressInterval={15000}
                          config={{
                            file: {
                              attributes: { controlsList: "nodownload" },
                            },
                          }}
                          onProgress={(state) => {
                            updateLastSeen(state.playedSeconds)
                          }}
                        />
                        {lesson.slides ? (
                          <div className="pt-2 flex justify-end">
                            <Button
                              as="a"
                              href={lesson.slides}
                              target="_blank"
                              type="button"
                              className="w-fit mt-3 text-white bg-gradient-to-br from-red-500 via-red-logo to-red-900 hover:bg-gradient-to-bl !border-red-logo focus:ring-red-900 dark:focus:ring-red-900 focus:!ring-2"
                            >
                              <BiDownload className="mr-2" />
                              Download slides
                            </Button>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                      {lesson.references.length > 0 ? (
                        <Card
                          className="mx-3 md:mx-20 mt-6 md:mt-10"
                          theme={{
                            root: {
                              base: "flex rounded-lg border bg-white dark:bg-neutral-900 shadow-md border-neutral-300 dark:border-neutral-700 dark:bg-neutral-900 flex-col",
                              children:
                                "flex h-full flex-col justify-center gap-0 p-0",
                            },
                          }}
                        >
                          <Accordion
                            alwaysOpen={true}
                            collapseAll={true}
                            flush={true}
                            className="!border-b-0 !divide-neutral-300 !border-neutral-300 dark:!divide-neutral-700 dark:!border-neutral-700"
                            theme={{
                              root: {
                                base: "divide-y divide-neutral-300 border-neutral-300 dark:divide-neutral-700 dark:border-neutral-700 border-b",
                              },
                            }}
                          >
                            <Accordion.Panel>
                              <Accordion.Title
                                theme={{
                                  base: "flex w-full items-center justify-between first:rounded-t-lg last:rounded-b-lg py-5 px-5 text-left font-medium text-neutral-800 dark:text-white !bg-transparent dark:!bg-transparent",
                                }}
                              >
                                <div className=" flex text-2xl font-bold tracking-tight text-neutral-800 dark:text-white">
                                  <BiLinkAlt className="mr-3" />
                                  Resources
                                </div>
                              </Accordion.Title>
                              <Accordion.Content
                                theme={{
                                  base: "py-5 px-5 last:rounded-b-lg border-neutral-100 bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-700 first:rounded-t-lg",
                                }}
                              >
                                <ul className="list-disc list-inside">
                                  {lesson.references.map(
                                    (reference: any, index) => (
                                      <li
                                        key={index}
                                        className="text-neutral-700 dark:text-white"
                                      >
                                        <a
                                          href={reference}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center font-medium text-red-logo dark:text-red-500 hover:text-red-900 hover:dark:text-red-800"
                                        >
                                          <BiLinkExternal className="mr-1" />
                                          {reference}
                                        </a>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </Accordion.Content>
                            </Accordion.Panel>
                          </Accordion>
                        </Card>
                      ) : (
                        <></>
                      )}
                      {lesson.flags.length === 0 ? (
                        <></>
                      ) : (
                        <Card
                          className="mx-3 md:mx-20 mt-6 md:mt-10"
                          theme={{
                            root: {
                              base: "flex rounded-lg border border-neutral-200 bg-white shadow-md dark:border-neutral-900 dark:bg-neutral-900 flex-col mx-3 md:mx-20 mt-6 md:mt-10",
                            },
                          }}
                        >
                          <div className="flex">
                            <h5 className="flex text-2xl font-bold tracking-tight text-neutral-800 dark:text-white">
                              <FaFlask className="mr-3" />
                              Labs
                            </h5>

                            <Tooltip
                              content={
                                {
                                  LOCKED:
                                    "Lab provisioning can take up to 30 minutes",
                                  PROVISIONING:
                                    "Lab provisioning can take up to 30 minutes",
                                  PROVISIONED: "",
                                  DIRTY: "",
                                  NUKING: "",
                                  NUKED: "",
                                }[
                                  (labData?.data as any).state?.aws
                                    ?.state as string
                                ] ??
                                "Lab provisioning starts automatically once the voucher is activated"
                              }
                              placement="top"
                              theme={{
                                target: "my-auto",
                              }}
                              className={
                                {
                                  LOCKED: "font-sans",
                                  PROVISIONING: "font-sans",
                                  PROVISIONED: "font-sans invisible opacity-0",
                                  DIRTY: "font-sans invisible opacity-0",
                                  NUKING: "font-sans invisible opacity-0",
                                  NUKED: "font-sans invisible opacity-0",
                                }[
                                  (labData?.data as any).state?.aws
                                    ?.state as string
                                ] ?? "font-sans"
                              }
                            >
                              <Badge
                                className="flex justify-center md:justify-start mt-2 md:mt-0 md:ml-3 md:px-3 md:my-auto"
                                color={
                                  {
                                    LOCKED: "warning",
                                    PROVISIONING: "warning",
                                    PROVISIONED: "success",
                                    DIRTY: "failure",
                                    NUKING: "warning",
                                    NUKED: "failure",
                                  }[
                                    (labData?.data as any).state?.aws
                                      ?.state as string
                                  ] ?? "info"
                                }
                              >
                                {{
                                  LOCKED: "Initiating lab provisioning",
                                  PROVISIONING: "Provisioning lab",
                                  PROVISIONED:
                                    "Lab provisioned (" +
                                    (labData?.data as any).state?.aws
                                      ?.account_id +
                                    ")",
                                  DIRTY: "Error provisioning lab",
                                  NUKING: "Destroying lab",
                                  NUKED: "Lab destroyed",
                                }[
                                  (labData?.data as any).state?.aws
                                    ?.state as string
                                ] ?? "Lab not provisioned"}
                              </Badge>
                            </Tooltip>
                          </div>
                          {lesson.flags.map((flag) => (
                            <ClassroomFlag
                              key={flag.id}
                              flag={flag}
                              tf_outputs={
                                (labData?.data as any).state?.aws?.tf_outputs
                              }
                              onChange={(completed: boolean) => {
                                if (completed) {
                                  if (!completedFlagIds.includes(flag.id)) {
                                    completedFlagIds.push(flag.id)
                                  }
                                  if (
                                    Object.values(lesson.flags)
                                      .map((flag: any) => {
                                        return flag
                                      })
                                      .every((f: any) => {
                                        return completedFlagIds.includes(f.id)
                                      })
                                  ) {
                                    setAllFlagsCompleted(true)
                                  }
                                }
                              }}
                            />
                          ))}
                        </Card>
                      )}

                      <div className={!errCompleteMsg ? "sr-only" : ""}>
                        <Alert
                          className="max-w-md mx-3 md:mx-20 mt-6 md:mt-10"
                          color="failure"
                          icon={ExclamationTriangleIcon}
                          onDismiss={function onDismiss() {
                            setErrCompleteMsg("")
                          }}
                        >
                          <span ref={errCompleteRef}>{errCompleteMsg}</span>
                        </Alert>
                      </div>
                      {(courseData.data as any).sections &&
                      (courseData.data as any).sections[section.id] &&
                      (courseData.data as any).sections[section.id].lessons &&
                      (courseData.data as any).sections[section.id].lessons[
                        lesson.id
                      ] &&
                      (courseData.data as any).sections[section.id].lessons[
                        lesson.id
                      ].completed ? (
                        <>
                          {section.lessons
                            .map((l: any) => {
                              return (
                                (courseData.data as any).sections[section.id]
                                  .lessons[l.id] &&
                                (courseData.data as any).sections[section.id]
                                  .lessons[l.id].completed
                              )
                            })
                            .every((s: any) => {
                              return s
                            }) ? (
                            <div className="mx-3 md:mx-20 mt-6 md:mt-10">
                              <Alert color="success">
                                <LuPartyPopper className="inline mr-2" />
                                Congratulations you have completed all the
                                lessons in {section.title}
                              </Alert>
                            </div>
                          ) : (
                            <></>
                          )}
                          {course.sections === undefined ||
                          course.sections === null ? (
                            <></>
                          ) : (
                            <>
                              {course.sections[course.sections.length - 1]
                                .lessons[
                                course.sections[course.sections.length - 1]
                                  .lessons.length - 1
                              ].id === lesson.id ? (
                                <></>
                              ) : (
                                <div className="mx-3 md:mx-20 mt-6 md:mt-10">
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      nextLesson()
                                    }}
                                    className="text-white bg-gradient-to-br from-red-500 via-red-logo to-red-900 hover:bg-gradient-to-bl !border-red-logo focus:ring-red-900 dark:focus:ring-red-900 focus:!ring-2"
                                  >
                                    Next lesson
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <div className="mx-3 md:mx-20 mt-6 md:mt-10">
                          <Tooltip
                            className={allFlagsCompleted ? "opacity-0" : ""}
                            placement="right"
                            content="All flags must be submitted to mark the lesson as complete"
                          >
                            <Button
                              type="button"
                              onClick={() => {
                                completeLesson()
                              }}
                              disabled={!allFlagsCompleted}
                              className="text-white bg-gradient-to-br from-red-500 via-red-logo to-red-900 hover:bg-gradient-to-bl !border-red-logo focus:ring-red-900 dark:focus:ring-red-900 focus:!ring-2"
                            >
                              Complete lesson
                            </Button>
                          </Tooltip>
                        </div>
                      )}
                    </section>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </>
  )
}

export default Classroom
